'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

export type ToastSeverity = 'success' | 'warning' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'ghost';
}

export interface Toast {
  id: string;
  title: string;
  message?: string;
  severity: ToastSeverity;
  duration?: number; // ms, 0 = no auto-dismiss
  actions?: ToastAction[];
  timestamp: number;
  category?: 'order' | 'position' | 'bracket' | 'backtest' | 'system';
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id' | 'timestamp'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
  soundEnabled: boolean;
  setSoundEnabled: (v: boolean) => void;
  // Convenience helpers
  toastOrderFill: (symbol: string, side: string, qty: number, price: number, actions?: ToastAction[]) => void;
  toastPositionEntry: (symbol: string, side: string, qty: number, price: number) => void;
  toastBracketTrigger: (symbol: string, type: 'target' | 'stop', price: number) => void;
  toastMaxLossBreach: (currentLoss: number, limit: number) => void;
  toastBacktestComplete: (name: string, totalReturn: number, sharpe: number) => void;
  toastBacktestError: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  addToast: () => '',
  removeToast: () => {},
  clearAll: () => {},
  soundEnabled: true,
  setSoundEnabled: () => {},
  toastOrderFill: () => {},
  toastPositionEntry: () => {},
  toastBracketTrigger: () => {},
  toastMaxLossBreach: () => {},
  toastBacktestComplete: () => {},
  toastBacktestError: () => {},
});

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

// Sound synthesis using Web Audio API
function playToastSound(severity: ToastSeverity, enabled: boolean) {
  if (!enabled || typeof window === 'undefined') return;
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    const configs: Record<ToastSeverity, { freq: number; freq2: number; type: OscillatorType }> = {
      success: { freq: 523, freq2: 659, type: 'sine' },
      info: { freq: 440, freq2: 440, type: 'sine' },
      warning: { freq: 349, freq2: 294, type: 'triangle' },
      error: { freq: 220, freq2: 196, type: 'sawtooth' },
    };
    const cfg = configs[severity];
    osc.type = cfg.type;
    osc.frequency.setValueAtTime(cfg.freq, ctx.currentTime);
    osc.frequency.setValueAtTime(cfg.freq2, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.08, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {
    // Ignore audio errors
  }
}

let toastIdCounter = 0;
function genId() { return `toast-${++toastIdCounter}-${Date.now()}`; }

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem('apexfi-toast-sound');
    return stored === null ? true : stored === 'true';
  });
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const setSoundEnabled = useCallback((v: boolean) => {
    setSoundEnabledState(v);
    localStorage.setItem('apexfi-toast-sound', String(v));
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) { clearTimeout(timer); timersRef.current.delete(id); }
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id' | 'timestamp'>): string => {
    const id = genId();
    const duration = toast.duration ?? 5000;
    const newToast: Toast = { ...toast, id, timestamp: Date.now() };

    setToasts(prev => {
      // Max 5 toasts at once
      const updated = [newToast, ...prev].slice(0, 5);
      return updated;
    });

    playToastSound(toast.severity, soundEnabled);

    if (duration > 0) {
      const timer = setTimeout(() => removeToast(id), duration);
      timersRef.current.set(id, timer);
    }
    return id;
  }, [soundEnabled, removeToast]);

  const clearAll = useCallback(() => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current.clear();
    setToasts([]);
  }, []);

  // Convenience helpers
  const toastOrderFill = useCallback((symbol: string, side: string, qty: number, price: number, actions?: ToastAction[]) => {
    addToast({
      title: `Order Filled — ${symbol}`,
      message: `${side.toUpperCase()} ${qty} @ $${price.toFixed(2)}`,
      severity: 'success',
      category: 'order',
      duration: 6000,
      actions: actions ?? [],
    });
  }, [addToast]);

  const toastPositionEntry = useCallback((symbol: string, side: string, qty: number, price: number) => {
    addToast({
      title: `Position Opened — ${symbol}`,
      message: `${side === 'long' ? 'Long' : 'Short'} ${qty} shares @ $${price.toFixed(2)}`,
      severity: 'info',
      category: 'position',
      duration: 5000,
    });
  }, [addToast]);

  const toastBracketTrigger = useCallback((symbol: string, type: 'target\' | \'stop', price: number) => {
    addToast({
      title: type === 'target' ? `🎯 Target Hit — ${symbol}` : `🛑 Stop Triggered — ${symbol}`,
      message: `Bracket OCO exit at $${price.toFixed(2)}`,
      severity: type === 'target' ? 'success' : 'warning',
      category: 'bracket',
      duration: 7000,
    });
  }, [addToast]);

  const toastMaxLossBreach = useCallback((currentLoss: number, limit: number) => {
    addToast({
      title: '⚠️ Max Daily Loss Breached',
      message: `Loss $${Math.abs(currentLoss).toFixed(2)} exceeded limit $${limit.toFixed(2)}. Trading blocked.`,
      severity: 'error',
      category: 'position',
      duration: 0, // Persistent
    });
  }, [addToast]);

  const toastBacktestComplete = useCallback((name: string, totalReturn: number, sharpe: number) => {
    addToast({
      title: `✅ Backtest Complete — ${name}`,
      message: `Return: ${totalReturn >= 0 ? '+' : ''}${totalReturn.toFixed(1)}% | Sharpe: ${sharpe.toFixed(2)}`,
      severity: totalReturn >= 0 ? 'success' : 'warning',
      category: 'backtest',
      duration: 8000,
    });
  }, [addToast]);

  const toastBacktestError = useCallback((message: string) => {
    addToast({
      title: '❌ Backtest Failed',
      message,
      severity: 'error',
      category: 'backtest',
      duration: 6000,
    });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{
      toasts, addToast, removeToast, clearAll,
      soundEnabled, setSoundEnabled,
      toastOrderFill, toastPositionEntry, toastBracketTrigger,
      toastMaxLossBreach, toastBacktestComplete, toastBacktestError,
    }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} onClearAll={clearAll} />
    </ToastContext.Provider>
  );
};

// ─── Toast Container UI ────────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<ToastSeverity, { bar: string; icon: string; iconName: string; bg: string; border: string; title: string }> = {
  success: {
    bar: 'bg-[var(--color-success)]',
    icon: 'text-[var(--color-success)]',
    iconName: '✓',
    bg: 'bg-[var(--color-card)]',
    border: 'border-[var(--color-success)]/40',
    title: 'text-[var(--color-foreground)]',
  },
  warning: {
    bar: 'bg-[var(--color-warning)]',
    icon: 'text-[var(--color-warning)]',
    iconName: '⚠',
    bg: 'bg-[var(--color-card)]',
    border: 'border-[var(--color-warning)]/40',
    title: 'text-[var(--color-foreground)]',
  },
  error: {
    bar: 'bg-[var(--color-error)]',
    icon: 'text-[var(--color-error)]',
    iconName: '✕',
    bg: 'bg-[var(--color-card)]',
    border: 'border-[var(--color-error)]/40',
    title: 'text-[var(--color-foreground)]',
  },
  info: {
    bar: 'bg-[var(--color-primary)]',
    icon: 'text-[var(--color-primary)]',
    iconName: 'ℹ',
    bg: 'bg-[var(--color-card)]',
    border: 'border-[var(--color-primary)]/40',
    title: 'text-[var(--color-foreground)]',
  },
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
  onClearAll: () => void;
}

const ToastContainer = ({ toasts, onRemove, onClearAll }: ToastContainerProps) => {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[500] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.length > 1 && (
        <div className="flex justify-end">
          <button
            onClick={onClearAll}
            className="text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-financial px-2 py-1 rounded hover:bg-[var(--color-surface)]"
          >
            Clear all ({toasts.length})
          </button>
        </div>
      )}
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem = ({ toast, onRemove }: ToastItemProps) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Entrance animation
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const handleRemove = () => {
    setExiting(true);
    setTimeout(() => onRemove(toast.id), 250);
  };

  const styles = SEVERITY_STYLES[toast.severity];

  return (
    <div
      role="alert"
      className={`
        relative flex flex-col gap-2 p-3 rounded-lg border shadow-dropdown
        ${styles.bg} ${styles.border}
        transition-all duration-250 ease-out
        ${visible && !exiting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
      style={{ transitionProperty: 'opacity, transform' }}
    >
      {/* Left severity bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${styles.bar}`} />

      <div className="pl-3 flex items-start gap-2">
        {/* Icon */}
        <span className={`text-sm font-bold mt-0.5 shrink-0 ${styles.icon}`}>
          {styles.iconName}
        </span>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold leading-tight ${styles.title}`}>{toast.title}</p>
          {toast.message && (
            <p className="text-xs text-[var(--color-muted-foreground)] mt-0.5 leading-relaxed">{toast.message}</p>
          )}
        </div>

        {/* Close */}
        <button
          onClick={handleRemove}
          className="shrink-0 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-financial w-5 h-5 flex items-center justify-center rounded"
          aria-label="Dismiss notification"
        >
          <span className="text-xs">✕</span>
        </button>
      </div>

      {/* Action buttons */}
      {toast.actions && toast.actions.length > 0 && (
        <div className="pl-3 flex items-center gap-2 flex-wrap">
          {toast.actions.map((action, i) => (
            <button
              key={i}
              onClick={() => { action.onClick(); handleRemove(); }}
              className={`text-xs px-2.5 py-1 rounded font-medium transition-financial ${
                action.variant === 'ghost' ?'text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-surface)]' :'bg-[var(--color-primary)]/15 text-[var(--color-primary)] hover:bg-[var(--color-primary)]/25 border border-[var(--color-primary)]/30'
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Timestamp */}
      <div className="pl-3">
        <span className="text-xs text-[var(--color-muted-foreground)] font-data">
          {new Date(toast.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
        </span>
      </div>
    </div>
  );
};
